import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TrashBinIcon } from '@rsos/assets/icons';
import { Switch, Text, Box } from '@rsos/components/capstone/base';
import {
  deleteAppCIDR,
  selectUserOrgByApp,
  updateAppCIDR,
  selectManagementByApp,
} from '@rsos/sinatra';
import { notifyError } from '@rsos/utils/toastUtils';
import APP_NAME from '../../../constants/appName';
import { trackPauseIPAccessSuccess } from '../IPSecurityManagementTrackings';
import DeleteCidrModal from '../modals/DeleteCidrModal/';
import UpdateCidrModal from '../modals/UpdateCidrModal/';
import {
  IPAddressManagementTable,
  IPAddressManagementTableTD,
  IPAddressManagementTableTH,
  IPAddressManagementTableTR,
  IPAddressManagementTableBody,
  IPAddressManagementTableHead,
} from './IPAddressManagement.styles';
import IPAddressManagementEmptyState from './IPAddressManagementEmptyState';

const IPAddressManagement = () => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isUpdateInProgress, setUpdateInProgress] = useState(false);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const dispatch = useDispatch();
  const sinatra = useSelector(state => state.sinatra);
  const org = selectUserOrgByApp(sinatra, APP_NAME);
  const management = selectManagementByApp(sinatra, org.id, APP_NAME);
  const cidrRecords = management.whitelistedIPs.records;
  const { updating, deleting } = management.whitelistedIPs;

  const handleDelete = () => {
    dispatch(deleteAppCIDR(org.id, APP_NAME, selectedRecord.id))
      .catch(action => {
        const { error } = action;
        notifyError(error.response.data.detail);
      })
      .finally(() => {
        setIsDeleteModalVisible(prev => !prev);
      });
  };
  const handleDeleteRequest = record => {
    setIsDeleteModalVisible(prev => !prev);
    setSelectedRecord(record);
  };

  const handleUpdateRequest = record => {
    setSelectedRecord(record);
    setUpdateInProgress(true);
    if (record.enabled) {
      setIsUpdateModalVisible(prev => !prev);
    }
  };
  const handleUpdate = useCallback(() => {
    const updatedRecord = {
      ...selectedRecord,
      enabled: !selectedRecord.enabled,
    };
    return dispatch(updateAppCIDR(org.id, APP_NAME, updatedRecord))
      .then(() => {
        if (!updatedRecord.enabled) {
          trackPauseIPAccessSuccess();
        }
      })
      .catch(action => {
        const { error } = action;
        notifyError(error.response.data.detail);
      })
      .finally(() => {
        setSelectedRecord(null);
        setUpdateInProgress(false);
        if (selectedRecord.enabled) {
          setIsUpdateModalVisible(prev => !prev);
        }
      });
  }, [selectedRecord, dispatch, org]);

  useEffect(() => {
    if (selectedRecord && !selectedRecord.enabled && isUpdateInProgress) {
      handleUpdate();
    }
  }, [isUpdateInProgress, selectedRecord, handleUpdate]);

  return (
    <>
      <IPAddressManagementTable>
        <IPAddressManagementTableHead>
          <IPAddressManagementTableTR>
            <IPAddressManagementTableTH>IP Address</IPAddressManagementTableTH>
            <IPAddressManagementTableTH>Status</IPAddressManagementTableTH>
            <IPAddressManagementTableTH />
          </IPAddressManagementTableTR>
        </IPAddressManagementTableHead>
        <IPAddressManagementTableBody>
          {cidrRecords.length > 0 ? (
            cidrRecords.map(record => (
              <IPAddressManagementTableTR key={record.cidr}>
                <IPAddressManagementTableTD>
                  <Text size="normal" color="secondaryText" ellipsis>
                    {record.cidr}
                  </Text>
                </IPAddressManagementTableTD>
                <IPAddressManagementTableTD>
                  <Switch
                    isOn={record.enabled}
                    onOffLabel
                    onClick={() => handleUpdateRequest(record)}
                  />
                </IPAddressManagementTableTD>
                <IPAddressManagementTableTD>
                  <Box
                    color="secondaryText"
                    onClick={() => {
                      handleDeleteRequest(record);
                    }}
                    cursor="pointer"
                  >
                    <TrashBinIcon />
                  </Box>
                </IPAddressManagementTableTD>
              </IPAddressManagementTableTR>
            ))
          ) : (
            <IPAddressManagementEmptyState />
          )}
        </IPAddressManagementTableBody>
      </IPAddressManagementTable>
      <DeleteCidrModal
        isOpened={isDeleteModalVisible}
        onClose={() => {
          setIsDeleteModalVisible(prev => !prev);
        }}
        handleDelete={handleDelete}
        deleting={deleting}
      />
      <UpdateCidrModal
        isOpened={isUpdateModalVisible}
        onClose={() => {
          setIsUpdateModalVisible(prev => !prev);
        }}
        handleUpdate={handleUpdate}
        updating={updating}
      />
    </>
  );
};

export default IPAddressManagement;
